import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { TaskMemberRole } from '@graphql/generated';
import theme from '@themes/theme';

export const TaskRoleSelectorWeb = ({
  onRoleSelect,
  selectedRole,
  isOwner = true,
  onRemove,
  showRemove = false,
  isAssignee = false,
  isProjectEditor = false,
  isTeamMember = false,
}: {
  isOwner?: boolean;
  onRoleSelect: (role: TaskMemberRole) => void;
  selectedRole: TaskMemberRole;
  onRemove?: () => void;
  showRemove?: boolean;
  isAssignee?: boolean;
  isProjectEditor?: boolean;
  isTeamMember?: boolean;
}) => {
  const role1 = {
    title: 'Assignee',
    id: TaskMemberRole.Assignee,
  };
  const role2 = {
    title: 'Viewer',
    id: TaskMemberRole.Viewer,
  };

  // Project editors and team members can manage roles just like owners.
  // TA-9491: previously this was `isOwner` only, which collapsed the dropdown
  // to Viewer-only for editors/team members and hid it entirely on render.
  const canManageRoles = isOwner || isProjectEditor || isTeamMember;
  const roleOptions = canManageRoles ? [role1, role2] : [role2];

  const handleRolePress = (roleId: TaskMemberRole) => {
    onRoleSelect?.(roleId);
  };

  const [isHoveredRemove, setIsHoveredRemove] = useState(false);
  const [hoveredRoleId, setHoveredRoleId] = useState<TaskMemberRole | null>(
    null
  );

  return (
    <Box
      width={120}
      py='xxs'
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.grey02,
      }}>
      {roleOptions.map(({ title, id }) => {
        return (
          <TouchableOpacity
            key={id}
            onPress={() => handleRolePress(id)}
            onMouseEnter={() => setHoveredRoleId(id)}
            onMouseLeave={() => setHoveredRoleId(null)}>
            <Box
              p='xs'
              flex={1}
              flexDirection='row'
              height={32}
              alignItems='center'
              backgroundColor={hoveredRoleId === id ? 'grey01' : 'white'}>
              <Text
                flex={1}
                ml='xs'
                accessibilityLabel={title}
                variant='webBodySecondary'
                color='textPrimary'
                numberOfLines={1}>
                {title}
              </Text>
              {selectedRole === id && (
                <Icon name='Check' color='greenSecondary' variant='s' />
              )}
            </Box>
          </TouchableOpacity>
        );
      })}
      {(isOwner || isAssignee || isProjectEditor || isTeamMember) &&
        showRemove && (
          <Box
            style={{
              borderTopWidth: isProjectEditor || isTeamMember ? 0 : 1,
              borderTopColor: theme.colors.grey02,
            }}>
            <TouchableOpacity
              key={TaskMemberRole.Owner}
              onPress={() => onRemove?.()}
              onMouseEnter={() => setIsHoveredRemove(true)}
              onMouseLeave={() => setIsHoveredRemove(false)}>
              <Box
                p='xs'
                flex={1}
                flexDirection='row'
                height={32}
                alignItems='center'
                backgroundColor={isHoveredRemove ? 'grey01' : 'white'}>
                <Text
                  flex={1}
                  ml='xs'
                  accessibilityLabel='Remove'
                  variant='webBodySecondary'
                  color='alertRed'
                  numberOfLines={1}>
                  Remove
                </Text>
              </Box>
            </TouchableOpacity>
          </Box>
        )}
    </Box>
  );
};
